import { RefObject, useCallback, useEffect, useRef } from 'react'
import { VirtuosoHandle } from 'react-virtuoso'
import { ITemplateQuestionGroup } from '@core/entities/template/TemplateQuestionGroup'
import { E2EWindow, VirtuosoScope, VirtuosoTarget } from './virtuosoE2ETypes'

type Registration = {
  resolveTarget: (
    target: VirtuosoTarget,
  ) => { index: number; rowId: string } | null
  scope: VirtuosoScope
  scrollToIndex: (index: number) => void
}

const WINDOW_SCROLL_TOP_OFFSET = -88
const SCROLL_PARENT_TOP_OFFSET = -8
const registrations: Registration[] = []

const installE2EBridge = () => {
  const e2eWindow = window as E2EWindow
  const namespace = e2eWindow.__siteAssistE2E ?? {}

  e2eWindow.__siteAssistE2E = namespace
  namespace.virtuoso = {
    scrollToTarget: (target) => {
      // The most recently mounted matching list wins, so modal lists take
      // precedence over lists on the page behind them.
      for (let index = registrations.length - 1; index >= 0; index--) {
        const registration = registrations[index]
        if (registration.scope !== target.scope) continue

        const resolvedTarget = registration.resolveTarget(target)
        if (!resolvedTarget) continue

        registration.scrollToIndex(resolvedTarget.index)
        return resolvedTarget.rowId
      }

      return null
    },
  }
}

const registerE2EVirtuoso = (registration: Registration) => {
  registrations.push(registration)
  installE2EBridge()

  return () => {
    const registrationIndex = registrations.indexOf(registration)
    if (registrationIndex !== -1) {
      registrations.splice(registrationIndex, 1)
    }
  }
}

type UseVirtuosoScrollProps = {
  virtuosoRef: RefObject<VirtuosoHandle>
  rows: ReadonlyArray<ITemplateQuestionGroup>
  scope: VirtuosoScope
  scrollParent?: HTMLElement
  registerForE2E?: boolean
}

export const useVirtuosoScroll = ({
  virtuosoRef,
  rows,
  scope,
  scrollParent,
  registerForE2E = true,
}: UseVirtuosoScrollProps) => {
  const rowsRef = useRef(rows)
  rowsRef.current = rows

  const scrollToIndex = useCallback(
    (index: number) => {
      const offset = scrollParent
        ? SCROLL_PARENT_TOP_OFFSET
        : WINDOW_SCROLL_TOP_OFFSET

      const scroll = () => {
        virtuosoRef.current?.scrollToIndex({
          index,
          align: 'start',
          offset,
          // Instant scrolling is reliable for far-away rows that Virtuoso has
          // not measured yet; the second pass corrects dynamic row heights.
          behavior: 'auto',
        })
      }

      // Validation sets errors immediately before requesting the scroll. Wait
      // for React to commit those height changes, then correct the position
      // once Virtuoso has measured the newly mounted rows.
      setTimeout(scroll, 0)
      setTimeout(scroll, 150)
    },
    [scrollParent, virtuosoRef],
  )

  useEffect(() => {
    if (!Constants.E2E || !registerForE2E) return

    return registerE2EVirtuoso({
      scope,
      scrollToIndex,
      resolveTarget: (target) => {
        const currentRows = rowsRef.current
        let rowIndex = -1

        if (target.scope === 'template-builder') {
          rowIndex = target.index
        } else {
          rowIndex = currentRows.findIndex(
            (row) =>
              row.id === target.id ||
              row.data?.some((question) => question.id === target.id),
          )
        }

        const row = currentRows[rowIndex]
        return row ? { index: rowIndex, rowId: row.id } : null
      },
    })
  }, [registerForE2E, scope, scrollToIndex])

  return { scrollToIndex }
}
